/**
 * CLT (Brazilian Labor Law) validation utilities for vacation requests
 * Implements client-side validation rules according to Brazilian labor regulations
 */

import { VacationRequestType, ValidationResult } from '@/types/vacation'
import { calculateBusinessDays, meetsAdvanceNotice } from './date-utils'

/**
 * Validate vacation request against CLT rules (client-side)
 * @param startDate - Vacation start date
 * @param endDate - Vacation end date
 * @param type - Vacation request type
 * @param availableBalance - Available vacation days
 * @returns Validation result with errors array
 */
export function validateVacationRequest(
  startDate: Date,
  endDate: Date,
  type: VacationRequestType,
  availableBalance: number
): ValidationResult {
  const errors: string[] = []

  // Calculate business days
  const totalDays = calculateBusinessDays(startDate, endDate)

  // Rule 1: Start date must be at least 14 days in future
  if (!meetsAdvanceNotice(startDate)) {
    errors.push('Solicitação deve ser feita com pelo menos 14 dias de antecedência')
  }

  // Rule 2: End date must be after start date
  if (endDate <= startDate) {
    errors.push('Data de término deve ser posterior à data de início')
  }

  // Rule 3: Minimum days validation
  if (totalDays < 1) {
    errors.push('A solicitação deve ter pelo menos 1 dia útil')
  }

  // Rule 4: Full vacation minimum days
  if (type === VacationRequestType.FULL && totalDays < 5) {
    errors.push('Férias completas devem ter no mínimo 5 dias úteis')
  }

  // Rule 5: Sell Third requires exactly 10 business days
  if (type === VacationRequestType.SELL_THIRD && totalDays !== 10) {
    errors.push('Venda de 1/3 das férias requer exatamente 10 dias úteis')
  }

  // Rule 6: Sufficient balance
  if (totalDays > availableBalance) {
    errors.push(
      `Saldo insuficiente. Disponível: ${availableBalance} dias, Solicitado: ${totalDays} dias`
    )
  }

  // Rule 7: Maximum vacation period (30 days - CLT limit)
  if (totalDays > 30) {
    errors.push('Período de férias não pode exceder 30 dias úteis')
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Validate individual fields for real-time feedback
 */
export const validateField = {
  /**
   * Validate start date
   */
  startDate: (startDate: Date | null): string | null => {
    if (!startDate) {
      return 'Data de início é obrigatória'
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (startDate < today) {
      return 'Data de início não pode ser no passado'
    }

    if (!meetsAdvanceNotice(startDate)) {
      return 'Data de início deve ser pelo menos 14 dias no futuro'
    }

    return null
  },

  /**
   * Validate end date
   */
  endDate: (startDate: Date | null, endDate: Date | null): string | null => {
    if (!endDate) {
      return 'Data de término é obrigatória'
    }

    if (!startDate) {
      return null // Wait for start date to be valid first
    }

    if (endDate <= startDate) {
      return 'Data de término deve ser posterior à data de início'
    }

    return null
  },

  /**
   * Validate vacation type and days
   */
  type: (
    type: VacationRequestType | null,
    startDate: Date | null,
    endDate: Date | null
  ): string | null => {
    if (!type) {
      return 'Tipo de férias é obrigatório'
    }

    if (!startDate || !endDate) {
      return null // Wait for dates to be valid
    }

    const totalDays = calculateBusinessDays(startDate, endDate)

    if (type === VacationRequestType.FULL && totalDays < 5) {
      return 'Férias completas requerem no mínimo 5 dias úteis'
    }

    if (type === VacationRequestType.SELL_THIRD && totalDays !== 10) {
      return 'Venda de 1/3 requer exatamente 10 dias úteis'
    }

    return null
  },

  /**
   * Validate notes length
   */
  notes: (notes: string): string | null => {
    if (notes.length > 500) {
      return `Observações não podem exceder 500 caracteres (${notes.length}/500)`
    }
    return null
  }
}

/**
 * Get vacation type label in Portuguese
 */
export function getVacationTypeLabel(type: VacationRequestType): string {
  switch (type) {
    case VacationRequestType.FULL:
      return 'Férias Completas'
    case VacationRequestType.SPLIT:
      return 'Férias Fracionadas'
    case VacationRequestType.SELL_THIRD:
      return 'Vender 1/3'
    default:
      return type
  }
}

/**
 * Get vacation type description
 */
export function getVacationTypeDescription(type: VacationRequestType): string {
  switch (type) {
    case VacationRequestType.FULL:
      return 'mínimo 5 dias'
    case VacationRequestType.SPLIT:
      return 'flexível'
    case VacationRequestType.SELL_THIRD:
      return '10 dias em dinheiro'
    default:
      return ''
  }
}
